import { useState, useEffect } from 'react';
import { HeroLanding } from '@/components/ui/hero-1';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { plans } from '@/lib/subscription-plans';
import { TestimonialsCarousel } from '@/components/landing/TestimonialsCarousel';
import { getPlanLimits } from '@/lib/plan-limits';
import logo from '@/assets/logo.webp';
import { 
  Check, 
  X,
  Timer, 
  BookOpen, 
  TrendingUp, 
  RefreshCcw, 
  BarChart3, 
  Target,
  Zap,
  Star,
  Crown,
  ArrowRight,
  Brain,
  Calendar,
  Award,
  Video,
  ClipboardList,
  Trophy,
  Flame
} from 'lucide-react';
import { SocialProofStrip } from '@/components/landing/SocialProofStrip';
import { StudyEfficiencyCalculator } from '@/components/landing/StudyEfficiencyCalculator';
import { FocusRailDemo } from '@/components/ui/demo';
import { SEO } from '@/components/seo/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1 }
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    { "@type": "Question", "name": "O FocusTrack é gratuito?", "acceptedAnswer": { "@type": "Answer", "text": "Sim! Oferecemos um plano gratuito com funcionalidades essenciais. Você também pode testar os planos pagos por 7 dias sem compromisso e sem precisar de cartão de crédito." } },
    { "@type": "Question", "name": "Como funciona o sistema de revisões espaçadas?", "acceptedAnswer": { "@type": "Answer", "text": "Ao registrar uma sessão de estudo com um conteúdo, o sistema agenda automaticamente revisões em intervalos crescentes (1, 3, 7 e 14 dias), baseado na curva de esquecimento de Ebbinghaus." } },
    { "@type": "Question", "name": "Posso usar o FocusTrack no celular?", "acceptedAnswer": { "@type": "Answer", "text": "Sim! A plataforma é totalmente responsiva e funciona em qualquer dispositivo — computador, tablet ou smartphone — direto pelo navegador, sem precisar instalar nada." } },
    { "@type": "Question", "name": "Como funciona o plano de estudos?", "acceptedAnswer": { "@type": "Answer", "text": "Você configura o peso de cada disciplina e o total de horas semanais disponíveis. O sistema distribui automaticamente os blocos de estudo. No plano Premium, nossa IA analisa seu desempenho semanal e recomenda qual disciplina priorizar." } },
    { "@type": "Question", "name": "Posso cancelar minha assinatura a qualquer momento?", "acceptedAnswer": { "@type": "Answer", "text": "Sim, sem burocracia. Você pode cancelar diretamente pela plataforma e continuará com acesso até o final do período já pago." } },
    { "@type": "Question", "name": "Como o FocusTrack me ajuda a passar em concursos?", "acceptedAnswer": { "@type": "Answer", "text": "Combinamos timer Pomodoro, gestão de disciplinas com pesos, acompanhamento de questões, revisões espaçadas, vídeo aulas, simulados e estatísticas detalhadas — tudo projetado para otimizar seu tempo e aumentar sua taxa de aprovação." } },
    { "@type": "Question", "name": "Posso registrar vídeo aulas no FocusTrack?", "acceptedAnswer": { "@type": "Answer", "text": "Sim! Ao registrar uma sessão como Vídeo Aula, você pode informar a URL do vídeo e a minutagem assistida. O sistema soma automaticamente quantas vídeo aulas e minutos você concluiu por conteúdo." } },
    { "@type": "Question", "name": "Posso cadastrar simulados?", "acceptedAnswer": { "@type": "Answer", "text": "Sim. O FocusTrack possui um gerenciador de simulados com suporte a múltipla escolha e pontuação CESPE (acerto desconta erro), incluindo banca, data e nota final." } }
  ]
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "FocusTrack", "item": "https://focus.sistemaslotus.com.br/landing" },
    { "@type": "ListItem", "position": 2, "name": "Planos", "item": "https://focus.sistemaslotus.com.br/landing#planos" }
  ]
};

export default function LandingPage() {
  const formatPrice = (price: number) => {
    return price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  // Injeta JSON-LD de FAQ e Breadcrumb no <head> para rich results no Google
  useEffect(() => {
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.id = 'faq-schema';
    faqScript.text = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'breadcrumb-schema';
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.getElementById('faq-schema')?.remove();
      document.getElementById('breadcrumb-schema')?.remove();
    };
  }, []);

  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'basico':
        return <Star className="h-6 w-6" />;
      case 'pro':
        return <Zap className="h-6 w-6" />;
      case 'premium':
        return <Crown className="h-6 w-6" />;
      default:
        return <Star className="h-6 w-6" />;
    }
  };

  const features = [
    {
      icon: Timer,
      title: 'Timer Pomodoro',
      description: 'Aumente sua produtividade com sessões de estudo cronometradas e pausas programadas.'
    },
    {
      icon: BookOpen,
      title: 'Gestão de Disciplinas',
      description: 'Organize seus estudos por matérias com pesos personalizados e acompanhe cada uma.'
    },
    {
      icon: Calendar,
      title: 'Plano de Estudos',
      description: 'Configure pesos por disciplina e distribua suas horas semanais de forma inteligente e equilibrada.'
    },
    {
      icon: Brain,
      title: 'Recomendação por IA',
      description: 'Receba sugestões inteligentes de qual disciplina estudar com base no seu progresso e déficit semanal.'
    },
    {
      icon: RefreshCcw,
      title: 'Sistema de Revisões',
      description: 'Revisões espaçadas baseadas em ciência para máxima retenção do conteúdo.'
    },
    {
      icon: BarChart3,
      title: 'Estatísticas Detalhadas',
      description: 'Visualize seu desempenho com gráficos, taxa de acerto e métricas avançadas.'
    },
    {
      icon: Target,
      title: 'Acompanhamento de Questões',
      description: 'Registre acertos e erros para identificar pontos de melhoria.'
    },
    {
      icon: TrendingUp,
      title: 'Análise de Progresso',
      description: 'Veja sua evolução ao longo do tempo e mantenha-se motivado.'
    },
    {
      icon: Video,
      title: 'Vídeo Aulas',
      description: 'Registre URL e minutagem das aulas assistidas e veja quantas você já concluiu por conteúdo.'
    },
    {
      icon: ClipboardList,
      title: 'Simulados',
      description: 'Cadastre simulados (múltipla escolha ou CESPE), registre acertos e acompanhe sua nota final.'
    },
    {
      icon: Trophy,
      title: 'Conquistas e Badges',
      description: '27 conquistas distribuídas em 6 categorias para gamificar sua jornada de estudos.'
    },
    {
      icon: Flame,
      title: 'Streak de Estudos',
      description: 'Acompanhe dias consecutivos estudando e receba alertas quando seu streak estiver em risco.'
    },
  ];

  const benefits = [
    {
      icon: Brain,
      title: 'Baseado em Ciência',
      description: 'Método de revisão espaçada comprovado cientificamente para aumentar a retenção.'
    },
    {
      icon: Calendar,
      title: 'Planejamento Automático',
      description: 'O sistema agenda suas revisões automaticamente para você nunca esquecer.'
    },
    {
      icon: Award,
      title: 'Resultados Comprovados',
      description: 'Milhares de estudantes aprovados em concursos e vestibulares.'
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="FocusTrack – Plataforma de Estudos para Concursos"
        description="Organize estudos, registre sessões, faça revisões espaçadas e tenha um plano com IA. Comece grátis."
        path="/landing"
      />
      {/* Hero Section with integrated header */}
      <HeroLanding
        logo={{
          src: logo,
          alt: "FocusTrack",
          companyName: "FocusTrack"
        }}
        navigation={[
          { name: 'Funcionalidades', href: '#funcionalidades' },
          { name: 'Planos', href: '#planos' },
          { name: 'FAQ', href: '#faq' },
          { name: 'Contato', href: '/contato' },
        ]}
        loginText="Entrar"
        loginHref="/auth"
        title="Saia do caos, entre no plano"
        description="O FocusTrack é o seu QG de estudos. Organize matérias, automatize revisões e sinta a evolução real rumo à sua aprovação."
        announcementBanner={{
          text: "🚀 Novo: Integração com IA para planejamento de estudos",
          linkText: "Saiba mais",
          linkHref: "#por-que"
        }}
        callToActions={[
          { text: "Começar Grátis", href: "/auth?mode=signup", variant: "primary" },
          { text: "Ver Planos", href: "#planos", variant: "secondary" }
        ]}
        titleSize="large"
        gradientColors={{
          from: "oklch(0.623 0.214 259.815)",
          to: "oklch(0.746 0.108 233.765)"
        }}
      />

      <main>
      <section className="container relative z-10 -mt-20">
        <SocialProofStrip />
      </section>

      {/* FocusRail - Interactive Feature Showcase */}
      <section id="funcionalidades" aria-label="Funcionalidades do FocusTrack" className="py-20 bg-muted/30">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl font-bold md:text-4xl">
              Conheça o FocusTrack{' '}
              <span className="text-primary">por dentro</span>
            </h2>
            <p className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto">
              Explore as telas e descubra como cada funcionalidade impulsiona seus estudos.
            </p>
          </motion.div>
          <FocusRailDemo />
        </div>
      </section>

      {/* Features Section */}
      <section aria-label="Detalhes das funcionalidades" className="py-20">
        <div className="container">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold md:text-4xl">
              Tudo que você precisa para{' '}
              <span className="text-primary">estudar melhor</span>
            </h2>
            <p className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto">
              Ferramentas poderosas projetadas para maximizar seu aprendizado e aprovação.
            </p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                transition={{ duration: 0.5 }}
              >
                <Card className="h-full border border-white/10 bg-white/5 backdrop-blur-md shadow-xl hover:shadow-primary/10 transition-all duration-500 hover:-translate-y-2">
                  <CardHeader>
                    <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center text-primary mb-4 shadow-inner">
                      <feature.icon className="h-6 w-6" />
                    </div>
                    <CardTitle className="text-xl font-bold tracking-tight">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground/80 text-sm leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <StudyEfficiencyCalculator />

      {/* Benefits Section */}
      <section id="por-que" aria-label="Por que escolher o FocusTrack" className="py-20">
        <div className="container">
          <div className="grid gap-12 lg:grid-cols-2 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.h2 
                variants={fadeInUp}
                transition={{ duration: 0.6 }}
                className="text-3xl font-bold md:text-4xl mb-6"
              >
                Por que escolher o{' '}
                <span className="text-primary">FocusTrack</span>?
              </motion.h2>
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <motion.div 
                    key={index} 
                    variants={fadeInUp}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex gap-4"
                  >
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                      <benefit.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{benefit.title}</h3>
                      <p className="text-muted-foreground">{benefit.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={scaleIn}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="aspect-video rounded-3xl bg-gradient-to-br from-primary/30 to-accent/30 border border-white/10 backdrop-blur-xl flex items-center justify-center shadow-2xl relative group overflow-hidden">
                <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                <div className="text-center p-8 relative z-10 transition-transform duration-700 group-hover:scale-110">
                  <motion.div 
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ 
                      type: "spring", 
                      stiffness: 100, 
                      delay: 0.3,
                      damping: 10
                    }}
                    className="text-7xl font-black text-primary mb-2 drop-shadow-lg"
                  >
                    +85%
                  </motion.div>
                  <p className="text-xl font-medium text-foreground/80">de taxa de aprovação técnica</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="planos" aria-label="Planos e preços" className="py-20 bg-muted/30">
        <div className="container">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold md:text-4xl">
              Planos que cabem no seu{' '}
              <span className="text-primary">bolso</span>
            </h2>
            <p className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto">
              Escolha o plano ideal para suas necessidades. Todos com garantia de 7 dias.
            </p>
          </motion.div>

          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center gap-3 rounded-full border border-border bg-card/50 backdrop-blur-sm px-4 py-2">
              <span className={`text-sm font-medium ${billingPeriod === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>Mensal</span>
              <button
                type="button"
                onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${billingPeriod === 'yearly' ? 'bg-primary' : 'bg-muted'}`}
                aria-label="Alternar entre cobrança mensal e anual"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-card shadow transition-transform ${billingPeriod === 'yearly' ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className={`text-sm font-medium flex items-center gap-2 ${billingPeriod === 'yearly' ? 'text-foreground' : 'text-muted-foreground'}`}>
                Anual
                <Badge variant="secondary" className="text-xs">-17%</Badge>
              </span>
            </div>
          </div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid gap-8 md:grid-cols-3 max-w-5xl mx-auto"
          >
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                variants={fadeInUp}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <Card 
                  className={`relative h-full !overflow-visible transition-all duration-500 hover:-translate-y-3 cursor-default border-white/10 bg-white/5 backdrop-blur-md ${plan.highlighted ? 'border-primary ring-2 ring-primary/20 shadow-2xl scale-105 z-10' : 'hover:shadow-xl'}`}
                >
                  {plan.highlighted && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                      Mais Popular
                    </Badge>
                  )}
                  <CardHeader className="text-center pb-2">
                    <div className={`mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-2 ${plan.highlighted ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                      {getPlanIcon(plan.id)}
                    </div>
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="text-center space-y-6">
                    <div>
                      <span className="text-4xl font-bold">
                        {formatPrice(billingPeriod === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice)}
                      </span>
                      <span className="text-muted-foreground">{billingPeriod === 'monthly' ? '/mês' : '/ano'}</span>
                      {billingPeriod === 'yearly' && (
                        <p className="text-xs text-muted-foreground mt-1">
                          equivale a {formatPrice(plan.yearlyPrice / 12)}/mês
                        </p>
                      )}
                    </div>
                    <ul className="space-y-3 text-left">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardFooter>
                    <Button 
                      className="w-full" 
                      variant={plan.highlighted ? 'default' : 'outline'}
                      asChild
                    >
                      <Link to="/auth?mode=signup">Começar Agora</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          <motion.p 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center mt-8 text-sm text-muted-foreground"
          >
            Economize até 2 meses pagando anualmente. Todos os planos incluem garantia de 7 dias.
          </motion.p>

          {/* Comparison Table */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={fadeInUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-16 max-w-5xl mx-auto"
          >
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold">Compare os planos em detalhe</h3>
              <p className="text-muted-foreground mt-2">Encontre o plano perfeito para o seu nível de dedicação</p>
            </div>
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className={`text-sm font-medium ${billingPeriod === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>Mensal</span>
              <button
                type="button"
                onClick={() => setBillingPeriod(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${billingPeriod === 'yearly' ? 'bg-primary' : 'bg-muted'}`}
                aria-label="Alternar entre cobrança mensal e anual"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-card shadow transition-transform ${billingPeriod === 'yearly' ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className={`text-sm font-medium ${billingPeriod === 'yearly' ? 'text-foreground' : 'text-muted-foreground'}`}>
                Anual
                <Badge variant="secondary" className="ml-1.5 text-[10px]">-17%</Badge>
              </span>
            </div>
            <div className="overflow-x-auto rounded-xl border bg-card">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-semibold text-foreground w-[200px]">Funcionalidade</th>
                    <th className="text-center p-4 font-semibold text-muted-foreground w-[130px]">Gratuito</th>
                    {plans.map(plan => (
                      <th key={plan.id} className={`text-center p-4 font-semibold w-[130px] ${plan.highlighted ? 'text-primary' : 'text-foreground'}`}>
                        {plan.name}
                        {plan.highlighted && <Badge className="ml-2 text-[10px]">Popular</Badge>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Preço */}
                  <tr className="border-b bg-primary/5">
                    <td className="p-4 font-bold text-foreground">
                      {billingPeriod === 'monthly' ? 'Preço mensal' : 'Preço anual'}
                    </td>
                    <td className="p-4 text-center">
                      <span className="text-lg font-bold text-foreground">Grátis</span>
                    </td>
                    {plans.map(plan => {
                      const price = billingPeriod === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
                      const suffix = billingPeriod === 'monthly' ? '/mês' : '/ano';
                      return (
                        <td key={plan.id} className="p-4 text-center">
                          <span className="text-lg font-bold text-foreground">{formatPrice(price)}</span>
                          <span className="text-xs text-muted-foreground block">{suffix}</span>
                        </td>
                      );
                    })}
                  </tr>
                  {/* Trial 7 dias */}
                  <tr className="border-b bg-muted/20">
                    <td className="p-4 font-medium text-foreground">Trial grátis de 7 dias</td>
                    <td className="p-4 text-center"><X className="h-4 w-4 text-muted-foreground/50 mx-auto" /></td>
                    {plans.map(plan => (
                      <td key={plan.id} className="p-4 text-center">
                        <Check className="h-4 w-4 text-accent mx-auto" />
                      </td>
                    ))}
                  </tr>

                  {/* SECTION: Limites de uso */}
                  <tr className="border-b">
                    <td colSpan={5} className="px-4 pt-5 pb-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary">Limites de uso</span>
                    </td>
                  </tr>
                  {/* Disciplinas */}
                  <tr className="border-b">
                    <td className="p-4 font-medium text-foreground">Disciplinas ativas</td>
                    <td className="p-4 text-center text-muted-foreground">{getPlanLimits('free').maxDisciplinas}</td>
                    {plans.map(plan => {
                      const limits = getPlanLimits(plan.id);
                      return (
                        <td key={plan.id} className="p-4 text-center font-medium text-foreground">
                          {limits.maxDisciplinas === null ? '∞ Ilimitadas' : limits.maxDisciplinas}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Sessões */}
                  <tr className="border-b bg-muted/20">
                    <td className="p-4 font-medium text-foreground">Sessões por mês</td>
                    <td className="p-4 text-center text-muted-foreground">{getPlanLimits('free').maxSessoesMes}</td>
                    {plans.map(plan => {
                      const limits = getPlanLimits(plan.id);
                      return (
                        <td key={plan.id} className="p-4 text-center font-medium text-foreground">
                          {limits.maxSessoesMes === null ? '∞ Ilimitadas' : limits.maxSessoesMes}
                        </td>
                      );
                    })}
                  </tr>

                  {/* SECTION: Estudo & Sessões */}
                  <tr className="border-b">
                    <td colSpan={5} className="px-4 pt-5 pb-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary">Estudo & Sessões</span>
                    </td>
                  </tr>
                  {/* Timer Pomodoro */}
                  <tr className="border-b">
                    <td className="p-4 font-medium text-foreground">Timer Pomodoro</td>
                    <td className="p-4 text-center"><Check className="h-4 w-4 text-accent mx-auto" /></td>
                    {plans.map(plan => (
                      <td key={plan.id} className="p-4 text-center"><Check className="h-4 w-4 text-accent mx-auto" /></td>
                    ))}
                  </tr>
                  {/* Vídeo aulas */}
                  <tr className="border-b bg-muted/20">
                    <td className="p-4 font-medium text-foreground">Vídeo aulas (URL + minutagem)</td>
                    <td className="p-4 text-center"><Check className="h-4 w-4 text-accent mx-auto" /></td>
                    {plans.map(plan => (
                      <td key={plan.id} className="p-4 text-center"><Check className="h-4 w-4 text-accent mx-auto" /></td>
                    ))}
                  </tr>
                  {/* Simulados */}
                  <tr className="border-b">
                    <td className="p-4 font-medium text-foreground">Simulados (múltipla escolha e CESPE)</td>
                    <td className="p-4 text-center"><Check className="h-4 w-4 text-accent mx-auto" /></td>
                    {plans.map(plan => (
                      <td key={plan.id} className="p-4 text-center"><Check className="h-4 w-4 text-accent mx-auto" /></td>
                    ))}
                  </tr>
                  {/* Histórico */}
                  <tr className="border-b bg-muted/20">
                    <td className="p-4 font-medium text-foreground">Histórico completo</td>
                    <td className="p-4 text-center"><Check className="h-4 w-4 text-accent mx-auto" /></td>
                    {plans.map(plan => (
                      <td key={plan.id} className="p-4 text-center"><Check className="h-4 w-4 text-accent mx-auto" /></td>
                    ))}
                  </tr>

                  {/* SECTION: Revisão & Progresso */}
                  <tr className="border-b">
                    <td colSpan={5} className="px-4 pt-5 pb-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary">Revisão & Progresso</span>
                    </td>
                  </tr>
                  {/* Sistema de revisões */}
                  <tr className="border-b">
                    <td className="p-4 font-medium text-foreground">Sistema de revisões espaçadas</td>
                    <td className="p-4 text-center"><X className="h-4 w-4 text-muted-foreground/50 mx-auto" /></td>
                    {plans.map(plan => {
                      const limits = getPlanLimits(plan.id);
                      return (
                        <td key={plan.id} className="p-4 text-center">
                          {limits.progressoDetalhado
                            ? <Check className="h-4 w-4 text-accent mx-auto" />
                            : <X className="h-4 w-4 text-muted-foreground/50 mx-auto" />}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Progresso detalhado */}
                  <tr className="border-b bg-muted/20">
                    <td className="p-4 font-medium text-foreground">Progresso detalhado por disciplina</td>
                    <td className="p-4 text-center"><X className="h-4 w-4 text-muted-foreground/50 mx-auto" /></td>
                    {plans.map(plan => {
                      const limits = getPlanLimits(plan.id);
                      return (
                        <td key={plan.id} className="p-4 text-center">
                          {limits.progressoDetalhado
                            ? <Check className="h-4 w-4 text-accent mx-auto" />
                            : <X className="h-4 w-4 text-muted-foreground/50 mx-auto" />}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Estatísticas avançadas */}
                  <tr className="border-b">
                    <td className="p-4 font-medium text-foreground">Estatísticas avançadas</td>
                    <td className="p-4 text-center"><X className="h-4 w-4 text-muted-foreground/50 mx-auto" /></td>
                    {plans.map(plan => {
                      const limits = getPlanLimits(plan.id);
                      return (
                        <td key={plan.id} className="p-4 text-center">
                          {limits.estatisticasAvancadas
                            ? <Check className="h-4 w-4 text-accent mx-auto" />
                            : <X className="h-4 w-4 text-muted-foreground/50 mx-auto" />}
                        </td>
                      );
                    })}
                  </tr>

                  {/* SECTION: Planejamento & IA */}
                  <tr className="border-b">
                    <td colSpan={5} className="px-4 pt-5 pb-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary">Planejamento & IA</span>
                    </td>
                  </tr>
                  {/* Plano de estudos com pesos */}
                  <tr className="border-b">
                    <td className="p-4 font-medium text-foreground">Plano de estudos com pesos</td>
                    <td className="p-4 text-center"><X className="h-4 w-4 text-muted-foreground/50 mx-auto" /></td>
                    {plans.map(plan => {
                      const limits = getPlanLimits(plan.id);
                      return (
                        <td key={plan.id} className="p-4 text-center">
                          {limits.cronogramaSemanal
                            ? <Check className="h-4 w-4 text-accent mx-auto" />
                            : <X className="h-4 w-4 text-muted-foreground/50 mx-auto" />}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Recomendação IA */}
                  <tr className="border-b bg-muted/20">
                    <td className="p-4 font-medium text-foreground">Recomendação por IA</td>
                    <td className="p-4 text-center"><X className="h-4 w-4 text-muted-foreground/50 mx-auto" /></td>
                    {plans.map(plan => {
                      const limits = getPlanLimits(plan.id);
                      return (
                        <td key={plan.id} className="p-4 text-center">
                          {limits.recomendacaoIA
                            ? <Check className="h-4 w-4 text-accent mx-auto" />
                            : <X className="h-4 w-4 text-muted-foreground/50 mx-auto" />}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Análise semanal por IA */}
                  <tr className="border-b">
                    <td className="p-4 font-medium text-foreground">Análise semanal de desempenho por IA</td>
                    <td className="p-4 text-center"><X className="h-4 w-4 text-muted-foreground/50 mx-auto" /></td>
                    {plans.map(plan => {
                      const limits = getPlanLimits(plan.id);
                      return (
                        <td key={plan.id} className="p-4 text-center">
                          {limits.recomendacaoIA
                            ? <Check className="h-4 w-4 text-accent mx-auto" />
                            : <X className="h-4 w-4 text-muted-foreground/50 mx-auto" />}
                        </td>
                      );
                    })}
                  </tr>

                  {/* SECTION: Engajamento */}
                  <tr className="border-b">
                    <td colSpan={5} className="px-4 pt-5 pb-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary">Engajamento</span>
                    </td>
                  </tr>
                  {/* Streak */}
                  <tr className="border-b">
                    <td className="p-4 font-medium text-foreground">Streak de estudos</td>
                    <td className="p-4 text-center"><Check className="h-4 w-4 text-accent mx-auto" /></td>
                    {plans.map(plan => (
                      <td key={plan.id} className="p-4 text-center"><Check className="h-4 w-4 text-accent mx-auto" /></td>
                    ))}
                  </tr>
                  {/* Conquistas */}
                  <tr className="border-b bg-muted/20">
                    <td className="p-4 font-medium text-foreground">Conquistas e badges (27)</td>
                    <td className="p-4 text-center"><Check className="h-4 w-4 text-accent mx-auto" /></td>
                    {plans.map(plan => (
                      <td key={plan.id} className="p-4 text-center"><Check className="h-4 w-4 text-accent mx-auto" /></td>
                    ))}
                  </tr>
                  {/* Atalhos */}
                  <tr className="border-b">
                    <td className="p-4 font-medium text-foreground">Atalhos globais (Ctrl+T / Ctrl+R)</td>
                    <td className="p-4 text-center"><Check className="h-4 w-4 text-accent mx-auto" /></td>
                    {plans.map(plan => (
                      <td key={plan.id} className="p-4 text-center"><Check className="h-4 w-4 text-accent mx-auto" /></td>
                    ))}
                  </tr>

                  {/* SECTION: Suporte */}
                  <tr className="border-b">
                    <td colSpan={5} className="px-4 pt-5 pb-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary">Suporte</span>
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-medium text-foreground">Suporte</td>
                    <td className="p-4 text-center text-muted-foreground">—</td>
                    <td className="p-4 text-center text-foreground">E-mail</td>
                    <td className="p-4 text-center text-foreground font-medium">Prioritário</td>
                    <td className="p-4 text-center text-foreground font-medium">VIP 24/7</td>
                  </tr>


                  {/* CTAs */}
                  <tr className="bg-muted/30">
                    <td className="p-5" />
                    <td className="p-5 text-center">
                      <Button size="sm" variant="outline" asChild className="w-full max-w-[140px]">
                        <Link to="/auth?mode=signup">Começar Grátis</Link>
                      </Button>
                    </td>
                    {plans.map(plan => (
                      <td key={plan.id} className="p-5 text-center">
                        <Button size="sm" variant={plan.highlighted ? 'default' : 'outline'} asChild className="w-full max-w-[140px]">
                          <Link to="/auth?mode=signup">
                            {plan.highlighted ? '🔥 Assinar' : 'Assinar'}
                          </Link>
                        </Button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsCarousel />

      {/* CTA Section */}
      <section aria-label="Chamada para ação" className="py-20 bg-muted/30">
        <div className="container">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={scaleIn}
            transition={{ duration: 0.7 }}
            className="mx-auto max-w-3xl text-center rounded-3xl bg-gradient-to-br from-primary to-accent p-12 text-primary-foreground"
          >
            <h2 className="text-3xl font-bold md:text-4xl mb-4">
              Pronto para transformar seus estudos?
            </h2>
            <p className="text-lg opacity-90 mb-8">
              Junte-se a milhares de estudantes que já estão usando o FocusTrack para alcançar seus objetivos.
            </p>
            <Button size="lg" variant="secondary" asChild className="text-lg px-8">
              <Link to="/auth?mode=signup">
                Começar Grátis Agora
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" aria-label="Perguntas frequentes" className="py-20 bg-muted/30">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold md:text-4xl">
              Perguntas <span className="text-primary">Frequentes</span>
            </h2>
            <p className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto">
              Tire suas dúvidas sobre o FocusTrack.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={fadeInUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="max-w-3xl mx-auto"
          >
            <Accordion type="single" collapsible className="space-y-3">
              {[
                {
                  q: 'O FocusTrack é gratuito?',
                  a: 'Sim! Oferecemos um plano gratuito com funcionalidades essenciais. Você também pode testar os planos pagos por 7 dias sem compromisso e sem precisar de cartão de crédito.',
                },
                {
                  q: 'Como funciona o sistema de revisões espaçadas?',
                  a: 'Ao registrar uma sessão de estudo com um conteúdo, o sistema agenda automaticamente revisões em intervalos crescentes (1, 3, 7 e 14 dias), baseado na curva de esquecimento de Ebbinghaus. Isso maximiza a retenção a longo prazo.',
                },
                {
                  q: 'Posso usar o FocusTrack no celular?',
                  a: 'Sim! A plataforma é totalmente responsiva e funciona em qualquer dispositivo — computador, tablet ou smartphone — direto pelo navegador, sem precisar instalar nada.',
                },
                {
                  q: 'Como funciona o plano de estudos?',
                  a: 'Você configura o peso de cada disciplina e o total de horas semanais disponíveis. O sistema distribui automaticamente os blocos de estudo de forma equilibrada. No plano Premium, nossa IA analisa seu desempenho semanal e recomenda qual disciplina priorizar.',
                },
                {
                  q: 'Posso cancelar minha assinatura a qualquer momento?',
                  a: 'Sim, sem burocracia. Você pode cancelar diretamente pela plataforma e continuará com acesso até o final do período já pago.',
                },
                {
                  q: 'Como o FocusTrack me ajuda a passar em concursos?',
                  a: 'Combinamos timer Pomodoro, gestão de disciplinas com pesos, acompanhamento de questões (acertos/erros), revisões espaçadas, vídeo aulas, simulados e estatísticas detalhadas — tudo projetado para otimizar seu tempo e aumentar sua taxa de aprovação.',
                },
                {
                  q: 'Posso registrar vídeo aulas no FocusTrack?',
                  a: 'Sim! Ao registrar uma sessão como Vídeo Aula, você pode informar a URL do vídeo e a minutagem assistida (início e fim). O sistema soma automaticamente quantas vídeo aulas e minutos você concluiu por conteúdo, mostrando esse indicador na página da disciplina.',
                },
                {
                  q: 'Posso cadastrar simulados?',
                  a: 'Sim. O FocusTrack possui um gerenciador completo de simulados com suporte a múltipla escolha e pontuação CESPE (acerto desconta erro), incluindo banca, data e nota final calculada automaticamente.',
                },
              ].map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="rounded-lg border bg-card px-6">
                  <AccordionTrigger className="text-left font-medium hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      </main>

      {/* Footer */}
      <motion.footer 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        transition={{ duration: 0.6 }}
        className="border-t py-12 bg-muted/30"
      >
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={logo} alt="FocusTrack" className="h-8" />
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} FocusTrack. Todos os direitos reservados.
            </p>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <Link to="/termos" className="hover:text-foreground transition-colors">Termos de Uso</Link>
              <Link to="/privacidade" className="hover:text-foreground transition-colors">Privacidade</Link>
              <Link to="/contato" className="hover:text-foreground transition-colors">Contato</Link>
            </div>
          </div>
        </div>
      </motion.footer>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/5527999616049"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-lg hover:bg-green-600 transition-all hover:scale-110"
        aria-label="Contato via WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="h-7 w-7 fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
}
